import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'pathSelector',
    'saveAsIsButton',
    'saveAsIsSpinner',
    'saveAsIsIcon',
    'refineButton',
    'nudge',
    'generatorModel',
    'nudgeForm',
    'generateButton',
    'generateSpinner',
    'generateIcon',
    'resultSection',
    'approvedContent',
    'approvedFunctionCall',
    'saveButton',
    'saveSpinner',
    'saveIcon',
    'alert',
    'alertMessage',
    'successState',
    'successId',
    'successStatus',
  ]

  static values = {
    generateUrl: String,
    createUrl: String,
    saveAsIsUrl: String,
    emptyNudgeMessage: String,
    emptyOutputMessage: String,
    invalidFunctionCallMessage: String,
    generateErrorMessage: String,
    saveErrorMessage: String,
  }

  async saveAsIs(event) {
    event.preventDefault()
    this._hideAlert()
    this._setBusy(this.saveAsIsButtonTarget, this.saveAsIsSpinnerTarget, this.saveAsIsIconTarget)
    this.refineButtonTarget.disabled = true

    try {
      const response = await this._post(this.saveAsIsUrlValue, {})
      this._showSuccess(response)
    } catch (e) {
      this._showAlert(`${this.saveErrorMessageValue} ${e.message}`)
      this._clearBusy(this.saveAsIsButtonTarget, this.saveAsIsSpinnerTarget, this.saveAsIsIconTarget)
      this.refineButtonTarget.disabled = false
    }
  }

  enterRefineMode(event) {
    event.preventDefault()
    this._hideAlert()
    this.pathSelectorTarget.classList.add('hidden')
    this.nudgeFormTarget.classList.remove('hidden')
    this.nudgeFormTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    this.nudgeTarget.focus()
  }

  async generate(event) {
    event.preventDefault()
    this._hideAlert()

    if (!this.nudgeTarget.value.trim()) {
      this._showAlert(this.emptyNudgeMessageValue)
      return
    }

    this._setBusy(this.generateButtonTarget, this.generateSpinnerTarget, this.generateIconTarget)

    try {
      const response = await this._post(this.generateUrlValue, {
        nudge: this.nudgeTarget.value,
        generator_model: this.generatorModelTarget.value,
      })

      this.approvedContentTarget.value = response.content || ''
      this.approvedFunctionCallTarget.value = response.function_call
        ? JSON.stringify(this._withParsedArgs(response.function_call), null, 2)
        : ''

      this.resultSectionTarget.classList.remove('hidden')
      this.saveButtonTarget.disabled = false
      this.resultSectionTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    } catch (e) {
      this._showAlert(`${this.generateErrorMessageValue} ${e.message}`)
    } finally {
      this._clearBusy(this.generateButtonTarget, this.generateSpinnerTarget, this.generateIconTarget)
    }
  }

  async save(event) {
    event.preventDefault()
    this._hideAlert()

    const content = this.approvedContentTarget.value
    const fcRaw = this.approvedFunctionCallTarget.value.trim()

    if (!content.trim() && !fcRaw) {
      this._showAlert(this.emptyOutputMessageValue)
      return
    }

    let functionCall = null
    if (fcRaw) {
      try {
        functionCall = JSON.parse(fcRaw)
      } catch (e) {
        this._showAlert(`${this.invalidFunctionCallMessageValue} ${e.message}`)
        return
      }
    }

    this._setBusy(this.saveButtonTarget, this.saveSpinnerTarget, this.saveIconTarget)

    const body = {
      nudge: this.nudgeTarget.value,
      generator_model: this.generatorModelTarget.value,
      'approved_output[content]': content,
    }
    if (functionCall) {
      body['approved_output[function_call]'] = JSON.stringify(functionCall)
    }

    try {
      const response = await this._post(this.createUrlValue, body)
      this._showSuccess(response)
    } catch (e) {
      this._showAlert(`${this.saveErrorMessageValue} ${e.message}`)
      this._clearBusy(this.saveButtonTarget, this.saveSpinnerTarget, this.saveIconTarget)
    }
  }

  tryAgain() {
    this._hideAlert()
    this.resultSectionTarget.classList.add('hidden')
    this.approvedContentTarget.value = ''
    this.approvedFunctionCallTarget.value = ''
    this.saveButtonTarget.disabled = true
    this.nudgeTarget.focus()
  }

  async _post(url, body) {
    const formData = new FormData()
    Object.entries(body).forEach(([k, v]) => formData.append(k, v))

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': this._csrfToken(),
        Accept: 'application/json',
      },
      body: formData,
    })

    if (!response.ok) {
      const text = await response.text()
      throw new Error(text || response.statusText)
    }
    return response.json()
  }

  _withParsedArgs(functionCall) {
    if (!functionCall || !functionCall.arguments) return functionCall
    if (typeof functionCall.arguments !== 'string') return functionCall
    try {
      return { ...functionCall, arguments: JSON.parse(functionCall.arguments) }
    } catch (e) {
      return functionCall
    }
  }

  _setBusy(button, spinner, icon) {
    button.disabled = true
    spinner.classList.remove('hidden')
    icon?.classList.add('hidden')
  }

  _clearBusy(button, spinner, icon) {
    button.disabled = false
    spinner.classList.add('hidden')
    icon?.classList.remove('hidden')
  }

  _showSuccess(response) {
    this.successIdTarget.textContent = `#${response.id}`
    this.successStatusTarget.textContent = response.status
    this.pathSelectorTarget.classList.add('hidden')
    this.nudgeFormTarget.classList.add('hidden')
    this.resultSectionTarget.classList.add('hidden')
    this.successStateTarget.classList.remove('hidden')
    this.successStateTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  _showAlert(message) {
    this.alertMessageTarget.textContent = message
    this.alertTarget.classList.remove('hidden')
  }

  _hideAlert() {
    this.alertTarget.classList.add('hidden')
  }
}
